import type { EventCategory } from '../types.js';

/**
 * Base event metadata shared across all event types.
 * Optimized for ClickHouse columnar storage with flat structure.
 */
export interface EventMetadata {
  // Event identification
  event_id: string;              // UUIDv7 for time-sorting
  event_type: string;            // Specific event type (LowCardinality in ClickHouse)
  event_category: EventCategory; // High-level category (LowCardinality)
  timestamp: number;             // DateTime64(3) - milliseconds since epoch

  // Session and user
  session_id: string;            // LowCardinality(String)
  user_id: string | null;        // Nullable(String)

  // Page context
  page_url: string;              // Full URL
  page_path: string;             // Path only (for grouping)
  page_title: string | null;     // Document title
  referrer: string | null;       // Nullable(String)

  // User agent and device
  user_agent: string;            // LowCardinality(String)
  viewport_width: number;        // UInt16
  viewport_height: number;       // UInt16
  screen_width: number;          // UInt16
  screen_height: number;         // UInt16
  device_pixel_ratio: number;    // Float32

  // Platform
  platform: string;              // LowCardinality(String)
  language: string;              // LowCardinality(String)
  timezone: string;              // LowCardinality(String)

  // Network (nullable as not always available)
  connection_type: string | null;      // Nullable(LowCardinality(String))
  connection_downlink: number | null;  // Nullable(Float32)
  connection_rtt: number | null;       // Nullable(UInt16)
}

/**
 * Generate base metadata from browser context
 */
export function generateBaseMetadata(
  sessionId: string,
  userId: string | null
): EventMetadata {
  const url = new URL(window.location.href);
  
  // Get connection info if available
  let connectionInfo: { type: string | null; downlink: number | null; rtt: number | null } = {
    type: null,
    downlink: null,
    rtt: null
  };
  
  if ('connection' in navigator) {
    const conn = (navigator as any).connection;
    if (conn) {
      connectionInfo = {
        type: conn.effectiveType || null,
        downlink: conn.downlink || null,
        rtt: conn.rtt || null
      };
    }
  }

  return {
    event_id: generateUUIDv7(),
    event_type: '',  // Will be set by specific event
    event_category: 'analytics',  // Will be set by specific event
    timestamp: Date.now(),
    session_id: sessionId,
    user_id: userId,
    page_url: window.location.href,
    page_path: url.pathname,
    page_title: document.title || null,
    referrer: document.referrer || null,
    user_agent: navigator.userAgent,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    screen_width: screen.width,
    screen_height: screen.height,
    device_pixel_ratio: window.devicePixelRatio,
    platform: navigator.platform,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    connection_type: connectionInfo.type,
    connection_downlink: connectionInfo.downlink,
    connection_rtt: connectionInfo.rtt
  };
}

/**
 * Generate UUIDv7 (time-ordered UUID)
 * Better for database indexing than random UUIDs
 */
function generateUUIDv7(): string {
  const timestamp = Date.now();
  const randomBytes = crypto.getRandomValues(new Uint8Array(10));
  
  // UUIDv7 format: timestamp (48 bits) + version (4 bits) + random (12 bits) + variant (2 bits) + random (62 bits)
  const timestampHex = timestamp.toString(16).padStart(12, '0');
  const randomHex = Array.from(randomBytes, b => b.toString(16).padStart(2, '0')).join('');
  
  const uuid = [
    timestampHex.slice(0, 8),
    timestampHex.slice(8, 12),
    '7' + randomHex.slice(0, 3),  // Version 7
    ((parseInt(randomHex.slice(3, 5), 16) & 0x3f) | 0x80).toString(16).padStart(2, '0') + randomHex.slice(5, 7),
    randomHex.slice(7, 19)
  ].join('-');
  
  return uuid;
}

